import * as z from "zod";

export const AB_TEST_ROADMAP_FEELINGS = [
  "love",
  "fine",
  "better",
  "unsure",
] as const;
export const AB_TEST_ROADMAP_GOALS = ["conv", "aov", "new", "margin"] as const;
export const AB_TEST_ROADMAP_LEAVE_ALONE = [
  "price",
  "free",
  "discounts",
  "options",
] as const;
export const AB_TEST_ROADMAP_BOLDNESS = [
  "careful",
  "balanced",
  "bold",
] as const;

const label = z.string().trim().min(1).max(120);

const RoadmapTest_ = z.object({
  id: z.string().regex(/^\d\.\d$/),
  title: label,
  /** Version A, the baseline. */
  baseline: label,
  testedLetter: z.string().regex(/^[B-Z]$/),
  /** The version the merchant picked to test against the baseline. */
  tested: label,
  weeks: z.number().int().min(1).max(52),
  /** Lift in revenue per checkout visit the estimate uses. */
  lift: z.number().min(0).max(1),
  /** Picked by the merchant from the library rather than by the answers. */
  addedByMerchant: z.boolean(),
});

const RoadmapTest: z.ZodType<RoadmapTest> = RoadmapTest_;
export interface RoadmapTest extends z.infer<typeof RoadmapTest_> {}

/**
 * A merchant confirmed their A/B test roadmap. Notifies the Conversion team;
 * nothing in the store's checkout changes.
 */
export const submitAbTestRoadmap = {
  name: "submitAbTestRoadmap" as const,
  input: z.object({
    answers: z.object({
      /** How they feel about their checkout today. */
      feeling: z.enum(AB_TEST_ROADMAP_FEELINGS),
      goal: z.enum(AB_TEST_ROADMAP_GOALS),
      /** Share of orders from new customers, as the merchant set it. */
      newShare: z.number().int().min(0).max(100),
      leave: z.array(z.enum(AB_TEST_ROADMAP_LEAVE_ALONE)).max(4),
      /** Whether the longest track records or the biggest upside go first. */
      boldness: z.enum(AB_TEST_ROADMAP_BOLDNESS),
      pace: z.union([z.literal(3), z.literal(5), z.literal(7)]),
    }),
    /** In run order. */
    tests: z.array(RoadmapTest).min(1).max(25),
    /** The numbers the estimates were shown with. */
    shown: z.object({
      averageOrderValue: z.number().nonnegative().nullable(),
      monthlyOrders: z.number().nonnegative().nullable(),
      weeklyCheckouts: z.number().nonnegative().nullable(),
    }),
  }),
  output: z.null(),
};
export type SubmitAbTestRoadmapInput = z.infer<
  typeof submitAbTestRoadmap.input
>;
